import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { leadsService } from '../../services/leadsService';
import Modal from '../common/Modal';
import './LeadsManagement.css';

const LeadsManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    page: 1,
    limit: 20
  });

  // Lead status options
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
    { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'quoted', label: 'Quoted', color: 'bg-purple-100 text-purple-800' },
    { value: 'booked', label: 'Booked', color: 'bg-green-100 text-green-800' },
    { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-800' }
  ];

  // Lead source options
  const sourceOptions = [
    { value: '', label: 'All Sources', icon: '📋' },
    { value: 'ai_concierge', label: 'Voyager AI', icon: '🤖' },
    { value: 'website_contact', label: 'Website Contact', icon: '🌐' },
    { value: 'website_quote_request', label: 'Quote Request', icon: '📝' },
    { value: 'wordpress_multisite', label: 'WordPress Site', icon: '🔗' },
    { value: 'content_card', label: 'Promo Card', icon: '🎴' },
    { value: 'website', label: 'Website (Legacy)', icon: '🌐' },
    { value: 'manual', label: 'Manual Entry', icon: '✍️' },
    { value: 'referral', label: 'Referral', icon: '👥' }
  ];

  // Load leads
  const loadLeads = async () => {
    try {
      setLoading(true);
      const response = await leadsService.getAll(filters);
      setLeads(response.data || []);
      setPagination(response.pagination || {});
      setError(null);
    } catch (err) {
      console.error('Error loading leads:', err);
      setError('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  // Load leads on component mount and filter changes
  useEffect(() => {
    loadLeads();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Open lead details modal
  const openLeadDetails = async (leadId) => {
    try {
      const response = await leadsService.getById(leadId);
      setSelectedLead(response.data);
      setShowModal(true);
    } catch (err) {
      console.error('Error loading lead details:', err);
      setError('Failed to load lead details');
    }
  };

  // Update lead status
  const updateLeadStatus = async (leadId, status, notes = '') => {
    try {
      await leadsService.updateStatus(leadId, { status, notes });
      // Refresh leads list
      await loadLeads();
      // Update selected lead if it's open
      if (selectedLead && selectedLead.id === leadId) {
        const response = await leadsService.getById(leadId);
        setSelectedLead(response.data);
      }
    } catch (err) {
      console.error('Error updating lead status:', err);
      setError('Failed to update lead status');
    }
  };

  // Convert lead to quote
  const convertToQuote = async (leadId) => {
    try {
      const response = await leadsService.convertToQuote(leadId);
      // Close modal and jump straight into the Quote Builder for the new
      // quote instead of just alerting - the quote now comes prefilled with
      // a properly typed component (for Voyager AI leads) so there's
      // immediate value in landing the agent there instead of the list.
      setShowModal(false);
      navigate(`/agent/quotes/${response.data.quoteId}`, { state: { fromLeadConversion: true } });
    } catch (err) {
      console.error('Error converting lead to quote:', err);
      setError('Failed to convert lead to quote');
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  // Get a friendly icon + label for a lead's source (falls back to the raw
  // value for any source not in sourceOptions, e.g. a future/unmapped one)
  const getSourceDisplay = (source) => {
    const sourceOption = sourceOptions.find(opt => opt.value === source);
    if (sourceOption && sourceOption.value) {
      return `${sourceOption.icon} ${sourceOption.label}`;
    }
    return source || 'Website';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format travel dates
  const formatTravelDates = (dates) => {
    if (!dates) return 'Not specified';

    // Handle if dates is already an object. Leads with no exact date (e.g.
    // Voyager AI/manual leads where the client only gave a vague timeframe
    // like "next spring") come through as {raw, start: null, end: null} -
    // see public-concierge.js's submitConciergeLead(). Fall back to the
    // freeform `raw` string in that case instead of falling through to the
    // String(dates) fallback below, which would print the literal text
    // "[object Object]" for any plain object.
    if (typeof dates === 'object') {
      if (dates.start && dates.end) {
        return `${new Date(dates.start).toLocaleDateString()} - ${new Date(dates.end).toLocaleDateString()}`;
      }
      if (dates.raw) {
        return dates.raw;
      }
      return 'Not specified';
    }

    // Handle if dates is a JSON string
    if (typeof dates === 'string') {
      try {
        const parsedDates = JSON.parse(dates);
        if (parsedDates.start && parsedDates.end) {
          return `${new Date(parsedDates.start).toLocaleDateString()} - ${new Date(parsedDates.end).toLocaleDateString()}`;
        }
        if (parsedDates.raw) {
          return parsedDates.raw;
        }
        // If it's a string but not JSON, return as is
        return dates;
      } catch (e) {
        // If JSON parsing fails, return the string as is
        return dates;
      }
    }

    // Fallback for any other primitive (number, boolean, etc.)
    return String(dates);
  };

  const filterChildrenAgesFromDetails = (details) => {
    if (!details) return '';
    
    // Ensure details is a string
    const detailsStr = typeof details === 'string' ? details : String(details);
    
    // Remove lines that contain "Children Ages:" or similar patterns
    return detailsStr
      .split('\n')
      .filter(line => {
        const lowerLine = line.toLowerCase();
        return !lowerLine.includes('children ages') && 
               !lowerLine.includes('child ages') &&
               !lowerLine.match(/ages?\s*:\s*\d/);
      })
      .join('\n')
      .trim();
  };

  const formatChildrenAges = (ages) => {
    if (!ages) return '';
    
    // Handle if ages is an array
    if (Array.isArray(ages)) {
      return ages.join(', ');
    }
    
    // Handle if ages is a JSON string
    if (typeof ages === 'string') {
      try {
        const parsedAges = JSON.parse(ages);
        if (Array.isArray(parsedAges)) {
          return parsedAges.join(', ');
        }
        return ages;
      } catch (e) {
        return ages;
      }
    }
    
    // Fallback - convert to string
    return String(ages);
  };

  return (
    <div className="leads-management">
      <div className="leads-header">
        <div className="leads-title-section">
          <h1 className="leads-title">Leads Management</h1>
          <p className="leads-subtitle">Manage and track your travel leads</p>
        </div>
      </div>

      {/* Filters */}
      <div className="leads-filters">
        <div className="filter-group">
          <label htmlFor="status-filter" className="filter-label">Status:</label>
          <select
            id="status-filter"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="source-filter" className="filter-label">Source:</label>
          <select
            id="source-filter"
            value={filters.source}
            onChange={(e) => handleFilterChange('source', e.target.value)}
            className="filter-select"
          >
            {sourceOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.icon} {option.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="limit-filter" className="filter-label">Per Page:</label>
          <select
            id="limit-filter"
            value={filters.limit}
            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            className="filter-select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading leads...</p>
        </div>
      )}

      {/* Leads Table */}
      {!loading && (
        <div className="leads-table-container">
          <table className="leads-table">
            <thead>
              <tr>
                <th className="client-column">Client Information</th>
                <th className="contact-column">Contact Details</th>
                <th className="travel-column">Travel Information</th>
                <th className="source-column">Source</th>
                <th className="status-column">Status</th>
                <th className="date-column">Created</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-leads">
                    No leads found. Leads will appear here when customers submit inquiries through your website.
                  </td>
                </tr>
              ) : (
                leads.map(lead => (
                  <tr key={lead.id} className="lead-row">
                    <td className="client-info-cell">
                      {lead.is_locked_upgrade_required ? (
                        <div className="lead-locked-overlay-wrapper">
                          <div className="lead-locked-blur">
                            <div className="client-name-primary">{lead.client_name}</div>
                            <div className="client-meta">Lead #{lead.id}</div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="client-name-primary">{lead.client_name}</div>
                          <div className="client-meta">
                            Lead #{lead.id}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="contact-info-cell">
                      {lead.is_locked_upgrade_required ? (
                        <div className="lead-locked-overlay-wrapper">
                          <div className="lead-locked-blur">
                            <div className="contact-email-primary">{lead.client_email}</div>
                            <div className="contact-phone-secondary">{lead.client_phone}</div>
                          </div>
                          <button
                            type="button"
                            className="lead-upgrade-unlock-btn"
                            onClick={() => navigate('/agent/activeagent-ai')}
                          >
                            🔒 Upgrade to Unlock
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="contact-email-primary">{lead.client_email}</div>
                          {lead.client_phone && (
                            <div className="contact-phone-secondary">{lead.client_phone}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="travel-info-cell">
                      <div className="travel-dates-primary">
                        📅 {formatTravelDates(lead.travel_dates)}
                      </div>
                      <div className="party-size-secondary">
                        👥 {lead.adults || 1} Adult{(lead.adults || 1) > 1 ? 's' : ''}
                        {lead.children > 0 && (
                          <span>, {lead.children} Child{lead.children > 1 ? 'ren' : ''}</span>
                        )}
                      </div>
                    </td>
                    <td className="source-info-cell">
                      <div className="source-type-primary">{getSourceDisplay(lead.source)}</div>
                      {lead.source_id && (
                        <div className="source-id-secondary">ID: {lead.source_id}</div>
                      )}
                    </td>
                    <td className="status-cell">
                      <span className={`status-badge ${getStatusColor(lead.status)}`}>
                        {statusOptions.find(opt => opt.value === lead.status)?.label || lead.status}
                      </span>
                    </td>
                    <td className="created-date-cell">
                      <div className="date-primary">
                        {new Date(lead.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                      <div className="time-secondary">
                        {new Date(lead.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="actions-cell">
                      <button
                        onClick={() => openLeadDetails(lead.id)}
                        className="action-btn view-btn"
                        title="View Details"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total leads)
          </span>
          
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.pages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Lead Details Modal */}
      {selectedLead && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Lead Details"
          maxWidth="1000px"
          footer={(
            <div className="lead-modal-footer">
              <div className="lead-modal-status-actions">
                <label htmlFor="status-update">Update Status:</label>
                <select
                  id="status-update"
                  value={selectedLead.status}
                  onChange={(e) => updateLeadStatus(selectedLead.id, e.target.value)}
                  className="lead-modal-status-select"
                >
                  {statusOptions.slice(1).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="lead-modal-action-buttons">
                {selectedLead.quote_id ? (
                  <button
                    onClick={() => {
                      setShowModal(false);
                      navigate(`/agent/quotes/${selectedLead.quote_id}`);
                    }}
                    className="lead-modal-convert-btn"
                  >
                    View Quote
                  </button>
                ) : selectedLead.status !== 'quoted' && selectedLead.status !== 'booked' && (
                  <button
                    onClick={() => convertToQuote(selectedLead.id)}
                    className="lead-modal-convert-btn"
                  >
                    Convert to Quote
                  </button>
                )}
                <button
                  onClick={() => setShowModal(false)}
                  className="lead-modal-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        >
          <div className="lead-details-container">
            {/* Client & Contact Information - Consolidated */}
            <div className="detail-section client-section">
              <h3>👤 Client & Contact Information</h3>
              <div className="client-details-grid">
                <div className="detail-item">
                  <label>Full Name:</label>
                  <span className="client-name-display">{selectedLead.client_name}</span>
                </div>
                <div className="detail-item">
                  <label>Email Address:</label>
                  <span className="contact-info">{selectedLead.client_email}</span>
                </div>
                <div className="detail-item">
                  <label>Phone Number:</label>
                  <span className="contact-info">{selectedLead.client_phone || 'Not provided'}</span>
                </div>
                <div className="detail-item">
                  <label>Lead Source:</label>
                  <span>{getSourceDisplay(selectedLead.source)}</span>
                </div>
                <div className="detail-item">
                  <label>Current Status:</label>
                  <span className={`status-badge ${getStatusColor(selectedLead.status)}`}>
                    {statusOptions.find(opt => opt.value === selectedLead.status)?.label || selectedLead.status}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Inquiry Date:</label>
                  <span>{formatDate(selectedLead.created_at)}</span>
                </div>
              </div>
            </div>

            {/* Travel Information */}
            <div className="detail-section travel-section">
              <h3>✈️ Travel Information</h3>
              <div className="travel-details-grid">
                <div className="detail-item">
                  <label>Travel Dates:</label>
                  <span className="travel-dates-display">{formatTravelDates(selectedLead.travel_dates)}</span>
                </div>
                <div className="detail-item">
                  <label>Adults:</label>
                  <span className="party-size">{selectedLead.adults || 1}</span>
                </div>
                <div className="detail-item">
                  <label>Children:</label>
                  <span className="party-size">{selectedLead.children || 0}</span>
                </div>
                {selectedLead.children > 0 && (
                  <div className="detail-item children-ages">
                    <label>Children Ages:</label>
                    <span className="ages-display">
                      {selectedLead.child_ages ?
                        `${formatChildrenAges(selectedLead.child_ages)} years old` :
                        'Not specified'
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Travel Requests & Details */}
            {(selectedLead.accommodation_requests || selectedLead.activity_requests || selectedLead.additional_details) && (
              <div className="detail-section requests-section">
                <h3>📝 Travel Requests & Details</h3>

                {selectedLead.accommodation_requests && (
                  <div className="request-item">
                    <h4>Accommodation Requests:</h4>
                    <p className="request-content">{selectedLead.accommodation_requests}</p>
                  </div>
                )}

                {selectedLead.activity_requests && (
                  <div className="request-item">
                    <h4>Activity Requests:</h4>
                    <p className="request-content">{selectedLead.activity_requests}</p>
                  </div>
                )}

                {selectedLead.additional_details && filterChildrenAgesFromDetails(selectedLead.additional_details) && (
                  <div className="request-item">
                    <h4>Additional Details:</h4>
                    <p className="request-content">{filterChildrenAgesFromDetails(selectedLead.additional_details)}</p>
                  </div>
                )}
              </div>
            )}

            {/* Agent Notes */}
            {selectedLead.agent_notes && (
              <div className="detail-section notes-section">
                <h3>📋 Agent Notes</h3>
                <p className="agent-notes-content">{selectedLead.agent_notes}</p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default LeadsManagement;
